/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Icon } from '../index'
import { useUser } from '../../hooks/useUser'
import { useAuthGuard } from '../../hooks/useAuthGuard'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { LoginPrompt } from '../Auth/LoginPrompt'
import { chatService } from '../../services/chatService'

function ChatWidget() {
  const { groupChatEnabled } = useFeatureFlags()
  const { isAuthenticated, requireAuth, loginPrompt, closePrompt } = useAuthGuard()
  const location = useLocation()
  const hasBottomBar = location.pathname.startsWith('/host') && !location.pathname.includes('/checkin')
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting') // connecting, connected, polling, error
  const [unreadCount, setUnreadCount] = useState(0)
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef(null)
  const { isLoggedIn, name, userId } = useUser()
  const lastReadTimestampRef = useRef(Date.now())

  const toggleChat = () => {
    if (!isAuthenticated) {
      requireAuth('chat')
      return
    }
    setIsOpen(!isOpen)
    if (!isOpen) {
      setUnreadCount(0)
      lastReadTimestampRef.current = Date.now()
    }
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Subscribe to messages with error handling
  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = chatService.subscribeToMessages(
      (newMessages, isSynced) => {
        setMessages(newMessages)
        setLoading(false)
        
        // Update connection status
        if (isSynced !== undefined) {
          setConnectionStatus(isSynced ? 'connected' : 'connecting')
        } else {
          setConnectionStatus('polling')
        }

        // Calculate unread count
        if (!isOpen) {
          const unread = newMessages.filter(
            m => m.username !== name && new Date(m.createdAt) > lastReadTimestampRef.current
          ).length
          setUnreadCount(unread)
        }
      },
      (err) => {
        console.error('Chat error:', err)
        setError('Connection issue - retrying...')
        setConnectionStatus('error')
      }
    )

    return unsubscribe
  }, [isLoggedIn, isOpen, name])

  // Send message with optimistic update
  const handleSend = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !isLoggedIn || isSending) return

    console.log('Sending message with:', { name, userId })

    const messageContent = newMessage.trim()
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      username: name,
      userId: userId,
      createdAt: new Date().toISOString(),
      _optimistic: true,
    }

    // Optimistic update
    setMessages(prev => [...prev, optimisticMessage])
    setNewMessage('')
    setIsSending(true)

    try {
      await chatService.sendMessage(messageContent, name, userId)
      // Real-time subscription will update with actual message
    } catch (error) {
      console.error('❌ Send failed:', error)
      
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id))
      setNewMessage(messageContent) // Restore message
      setError('Failed to send message')
      
      setTimeout(() => setError(null), 3000)
    } finally {
      setIsSending(false)
    }
  }

  const getStatusIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: '🟢', text: 'Live' }
      case 'polling':
        return { icon: '🟡', text: 'Polling' }
      case 'connecting':
        return { icon: '🟡', text: 'Connecting...' }
      case 'error':
        return { icon: '🔴', text: 'Reconnecting...' }
      default:
        return { icon: '⚪', text: '' }
    }
  }

  const status = getStatusIndicator()

  if (!groupChatEnabled) return null

  return (
    <>
      {/* Chat Button - Fixed bottom right, above footer */}
      <div className={`fixed right-4 sm:right-6 z-40 ${hasBottomBar ? 'bottom-40' : 'bottom-20'}`}>
        <button
          onClick={toggleChat}
          aria-label={isOpen ? 'Chat schließen' : 'Chat öffnen'}
          className="w-16 h-16 rounded-full bg-eggs-yellow text-eggs-dark transition-all duration-300 flex items-center justify-center"
        >
          {isOpen ? (
            <Icon name="x" size={24} className="text-white" />
          ) : (
            <Icon name="message-circle" size={24} className="text-white" />
          )}
        </button>
        {!isAuthenticated && (
          <div className="absolute -top-1 -right-1 bg-white text-eggs-dark text-sm rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
            <Icon name="lock" size={14} />
          </div>
        )}
        {unreadCount > 0 && !isOpen && isAuthenticated && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </div>

      {/* Chat Window */}
      {isOpen && isAuthenticated && (
        <div
          className="fixed bottom-24 right-4 sm:right-6 z-30"
          style={{
            width: 'min(400px, calc(100vw - 2rem))',
            height: '600px',
            animation: 'slideInUp 0.3s ease-out',
          }}
        >
          <div
            className="h-full rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
            }}
          >
            {/* Chat Header */}
            <div className="bg-eggs-yellow p-4 flex justify-between items-center">
              <div>
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Icon name="message-circle" size={20} /> Chat & eggsChange
                </h3>
                <div className="text-white text-xs opacity-90 flex items-center gap-1">
                  <span>{status.icon}</span>
                  <span>{status.text}</span>
                </div>
              </div>
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white font-bold">
                {name ? name.charAt(0).toUpperCase() : '?'}
              </div>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-2 text-sm">
                {error}
              </div>
            )}

            {/* Chat Messages */}
            <div
              className="flex-1 p-4 overflow-y-auto"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <div className="space-y-3">
                {!isLoggedIn ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>Authentifizierung läuft... 🔐</p>
                  </div>
                ) : loading ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>Loading chat... ⏳</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>No messages yet. Be the first!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.userId === userId ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-lg shadow-sm ${
                          msg.userId === userId
                            ? 'bg-eggs-yellow text-eggs-dark'
                            : 'bg-white text-gray-900'
                        } ${msg._optimistic ? 'opacity-60' : ''}`}
                      >
                        <div className="font-semibold text-xs mb-1 opacity-90">
                          {msg.username}
                        </div>
                        <div className="text-sm">{msg.content}</div>
                        <div className="text-xs opacity-70 mt-1">
                          {new Date(msg.createdAt).toLocaleTimeString('de-DE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <form onSubmit={handleSend} className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={isLoggedIn ? "Type a message..." : "Authenticating..."}
                  disabled={!isLoggedIn || isSending}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-eggs-yellow focus:border-transparent disabled:bg-gray-100"
                />
                <button
                  type="submit"
                  disabled={!isLoggedIn || !newMessage.trim() || isSending}
                  className="bg-eggs-yellow text-eggs-dark px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:bg-gray-400"
                >
                  {isSending ? '...' : 'Senden'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `,
        }}
      />

      {/* Login Prompt */}
      {loginPrompt && (
        <LoginPrompt feature={loginPrompt} onClose={closePrompt} />
      )}
    </>
  )
}

export default ChatWidget